/**
 * Motor de puntuacion — valores iniciales (seccion 8 de la especificacion).
 * Editable en local (RF-DEV-03); en produccion el administrador cambia estos
 * mismos valores desde el panel sin desplegar codigo (RF-PUNT-01).
 *
 * Este fichero es la fuente de verdad del SEED de la base de datos: los
 * valores viajan a `exercises` y `app_settings` en supabase/seed.sql.
 */

import type { CompetitionTheme, EnduranceFormat } from '@/types/domain';

/** Coeficiente de dificultad D del catalogo (RF-PUNT-01). Rango 1.0 – 8.0. */
export interface ExerciseSeed {
  code: string;
  name: { es: string; en: string };
  theme: CompetitionTheme;
  /** D — coeficiente de dificultad. */
  difficulty: number;
  /**
   * Marca de referencia: la marca que representa una ejecucion SOLIDA de este
   * movimiento, en la unidad que mida la tematica (repeticiones o segundos).
   *
   * Es lo que hace comparables ejercicios que no se parecen en nada. Sin ella,
   * R crece con la marca en bruto y un victorian de 3 segundos —que hacen unas
   * pocas decenas de personas en el mundo— puntuaba menos que 60 flexiones,
   * porque la marca alcanzable se desploma segun sube la dificultad mucho mas
   * deprisa de lo que D puede compensar.
   */
  reference: number;
  /** Si el ejercicio admite lastre (solo tiene efecto en tematicas estaticas). */
  allowsWeight: boolean;
}

/**
 * Catalogo inicial de ejercicios (tabla 8.2, ampliada).
 *
 * Dos reglas de catalogo:
 *
 *   - NINGUN ejercicio se hace en anillas. Se compite solo en barra, paralelas
 *     y suelo, que es material que cualquiera encuentra en un parque. Las
 *     anillas anaden una variable —la inestabilidad— que no se puede juzgar
 *     bien desde un video y que ademas excluye a quien no tiene con que
 *     entrenarlas.
 *   - El lastre solo existe donde se cuentan repeticiones. Ningun estatico lo
 *     admite: cargar un aguante mide la misma cualidad por dos caminos.
 *   - La escala de dificultad llega a 8.0, no a 6.0 como en la tabla original:
 *     el techo de 6.0 ya lo ocupaban planche y maltese, y sin margen por
 *     encima no cabian los estaticos de elite (victorian) ni los dinamicos
 *     mas duros. Los coeficientes de la especificacion se conservan intactos;
 *     lo nuevo se apila por encima.
 */
export const EXERCISE_CATALOG: ExerciseSeed[] = [
  // --- Dinamicos: de fundamentos a elite ------------------------------------
  { code: 'push_up', name: { es: 'Flexiones', en: 'Push-ups' }, theme: 'DYNAMIC', difficulty: 1.0, reference: 40, allowsWeight: true },
  { code: 'dip', name: { es: 'Fondos en paralelas', en: 'Parallel bar dips' }, theme: 'DYNAMIC', difficulty: 1.3, reference: 30, allowsWeight: true },
  { code: 'pull_up', name: { es: 'Dominadas (pronas, estrictas)', en: 'Pull-ups (pronated, strict)' }, theme: 'DYNAMIC', difficulty: 1.4, reference: 20, allowsWeight: true },
  { code: 'pistol_squat', name: { es: 'Pistol squat', en: 'Pistol squat' }, theme: 'DYNAMIC', difficulty: 1.6, reference: 20, allowsWeight: true },
  { code: 'muscle_up_bar', name: { es: 'Muscle-up en barra', en: 'Bar muscle-up' }, theme: 'DYNAMIC', difficulty: 2.4, reference: 10, allowsWeight: true },
  { code: 'hspu', name: { es: 'Pino-flexión (HSPU)', en: 'Handstand push-up (HSPU)' }, theme: 'DYNAMIC', difficulty: 2.8, reference: 8, allowsWeight: true },
  { code: 'one_arm_pull_up', name: { es: 'Dominada a un brazo', en: 'One-arm pull-up' }, theme: 'DYNAMIC', difficulty: 4.8, reference: 3, allowsWeight: false },
  { code: 'front_lever_touch', name: { es: 'Front lever touch', en: 'Front lever touch' }, theme: 'DYNAMIC', difficulty: 5.4, reference: 5, allowsWeight: false },
  { code: 'planche_push_up', name: { es: 'Flexión en planche', en: 'Planche push-up' }, theme: 'DYNAMIC', difficulty: 6.2, reference: 3, allowsWeight: false },

  // --- Estaticos ------------------------------------------------------------
  { code: 'l_sit', name: { es: 'L-sit', en: 'L-sit' }, theme: 'STATIC', difficulty: 1.8, reference: 30, allowsWeight: false },
  { code: 'handstand', name: { es: 'Pino libre', en: 'Freestanding handstand' }, theme: 'STATIC', difficulty: 2.6, reference: 60, allowsWeight: false },
  { code: 'tuck_planche', name: { es: 'Planche recogida (tuck)', en: 'Tuck planche' }, theme: 'STATIC', difficulty: 3.0, reference: 30, allowsWeight: false },
  { code: 'back_lever', name: { es: 'Back lever', en: 'Back lever' }, theme: 'STATIC', difficulty: 3.4, reference: 20, allowsWeight: false },
  { code: 'human_flag', name: { es: 'Human flag', en: 'Human flag' }, theme: 'STATIC', difficulty: 4.0, reference: 15, allowsWeight: false },
  { code: 'front_lever', name: { es: 'Front lever', en: 'Front lever' }, theme: 'STATIC', difficulty: 4.6, reference: 15, allowsWeight: false },
  { code: 'full_planche', name: { es: 'Planche extendida (full)', en: 'Full planche' }, theme: 'STATIC', difficulty: 6.0, reference: 8, allowsWeight: false },
  { code: 'maltese', name: { es: 'Maltese', en: 'Maltese' }, theme: 'STATIC', difficulty: 6.6, reference: 4, allowsWeight: false },
  { code: 'victorian', name: { es: 'Victorian', en: 'Victorian cross' }, theme: 'STATIC', difficulty: 8.0, reference: 3, allowsWeight: false },

  // --- Con lastre obligatorio (en barra y paralelas, nunca en anillas) ------
  // Antes aqui habia dos isometricas a 90°. Se van con el lastre: un aguante
  // lastrado mide la misma cualidad dos veces —cargas mas, aguantas menos— y
  // el sistema pagaba dos veces por ella. Con repeticiones no ocurre.
  { code: 'weighted_pull_up', name: { es: 'Dominada lastrada', en: 'Weighted pull-up' }, theme: 'DYNAMIC_WEIGHTED', difficulty: 2.0, reference: 8, allowsWeight: true },
  { code: 'weighted_dip', name: { es: 'Fondo lastrado en paralelas', en: 'Weighted parallel bar dip' }, theme: 'DYNAMIC_WEIGHTED', difficulty: 1.8, reference: 10, allowsWeight: true },

  // --- Resistencia ----------------------------------------------------------
  { code: 'pull_up_100_for_time', name: { es: '100 dominadas por tiempo', en: '100 pull-ups for time' }, theme: 'ENDURANCE', difficulty: 1.4, reference: 360, allowsWeight: false },
  { code: 'dips_amrap_10', name: { es: 'AMRAP 10 min de fondos', en: '10 min AMRAP dips' }, theme: 'ENDURANCE', difficulty: 1.3, reference: 100, allowsWeight: false },
];

/** Techo de la escala de dificultad. El panel valida contra este valor. */
export const MAX_DIFFICULTY_COEFFICIENT = 8.0;
export const MIN_DIFFICULTY_COEFFICIENT = 1.0;

/**
 * Escala de la normalizacion.
 *
 * R se mide contra la marca de referencia del ejercicio, no en bruto, de modo
 * que quien clava la referencia se lleva `D x ESCALA` puntos: 28 en dominadas,
 * 92 en front lever, 160 en victorian. La consecuencia es la que se busca —a
 * igualdad de logro, el movimiento mas duro renta mas— y ademas devuelve a D
 * su significado literal: la razon entre dos ejercicios ES la razon entre sus
 * coeficientes, cosa que antes no ocurria.
 */
export const NORMALIZED_SCALE = 20;

/** Suelo de la marca de referencia: una referencia de 0 dividiria por cero. */
export const MIN_REFERENCE_MARK = 0.1;

/**
 * Factor de rendimiento R por tematica (tabla 8.1).
 *
 * Estos factores convierten la marca declarada en un numero comparable DENTRO
 * de una misma tematica. La comparacion ENTRE ejercicios distintos la hace la
 * marca de referencia, no estos coeficientes.
 */
export const PERFORMANCE_FACTORS = {
  /** R = repeticiones x 2.0 */
  DYNAMIC_REP_FACTOR: 2.0,
  /** R = segundos x 1.0 */
  STATIC_SECOND_FACTOR: 1.0,
  /** R = repeticiones x 0.8 (formato AMRAP) */
  ENDURANCE_REP_FACTOR: 0.8,
  /** R = (tiempo_objetivo / tiempo_empleado) x 60 (formato por tiempo) */
  ENDURANCE_FOR_TIME_FACTOR: 60,
} as const;

/**
 * Multiplicador de lastre M — solo donde se cuentan repeticiones.
 *
 * El coeficiente subio de 0.8 a 1.5. Con 0.8, colgarse 20 kg siendo 75 —un 27%
 * mas de carga— pagaba un 21% mas de puntos, y eso no se sostiene: en un
 * isometrico la carga anadida no encarece el aguante en proporcion, lo hunde.
 * Un L-sit de 40 s lastrado no se parece en dificultad a uno sin lastre, y la
 * puntuacion tiene que notarlo.
 *
 * El tope sube de 2.0 a 2.5 en consecuencia: con el coeficiente nuevo, 2.0 se
 * alcanzaba ya con 50 kg sobre 75, y ahi todavia hay diferencias reales entre
 * atletas que merecen separarse.
 */
export const WEIGHT_MULTIPLIER = {
  /** M = 1 + (kg_lastre / peso_corporal) x COEFFICIENT */
  COEFFICIENT: 1.5,
  /** Tope duro para evitar marcas desproporcionadas (RF-PUNT-06). */
  MAX: 2.5,
  /** Sin lastre o tematica no estatica. */
  NEUTRAL: 1,
} as const;

/**
 * Bonificaciones que puede aplicar el revisor.
 *
 * Eran seis y quedan dos. Las cuatro que se fueron, y por que:
 *
 *   - PERSONAL_RECORD, CATEGORY_RECORD y FULL_PARTICIPATION estaban marcadas
 *     como "automaticas" pero NADIE las calculaba: no habia una sola linea que
 *     las concediese. En la practica eran casillas que el revisor marcaba a
 *     ojo, decidiendo a mano si una marca era un record personal. Eso es
 *     trabajo que deberia hacer el sistema, y mientras no lo haga es mejor no
 *     prometerlo.
 *   - FINAL_WEEK duplicaba el multiplicador de la propia competicion. La
 *     semana final ya viene con `score_multiplier = 2`, asi que marcar ademas
 *     el bonus daba x4. Ahora el doblaje vive en UN solo sitio.
 *
 * Las dos que quedan son juicios de verdad sobre el video, que es justo lo que
 * un revisor humano puede aportar y una formula no.
 */
export type BonusCode = 'CLEAN_EXECUTION' | 'TECHNICAL_PENALTY';

export interface BonusDefinition {
  code: BonusCode;
  value: number;
  /**
   * Las dos que quedan las aplica el revisor. Cuando alguna se calcule sola
   * —un record personal, por ejemplo— volvera a hacer falta distinguirlo.
   */
  source: 'admin';
}

/** B es el producto de las bonificaciones aplicables. */
export const BONUSES: Record<BonusCode, BonusDefinition> = {
  CLEAN_EXECUTION: { code: 'CLEAN_EXECUTION', value: 1.1, source: 'admin' },
  TECHNICAL_PENALTY: { code: 'TECHNICAL_PENALTY', value: 0.85, source: 'admin' },
};

/**
 * Tematicas que admiten lastre. Fuera de aqui, M = 1 siempre.
 *
 * OJO: esto invierte la regla original de la especificacion (RF-COMP-08), que
 * permitia lastre SOLO en estaticos. Ahora es al reves —solo donde se cuentan
 * repeticiones— y el motivo es que asi funciona el deporte: la calistenia
 * lastrada son dominadas y fondos con disco, no un front lever con chaleco.
 *
 * Ademas hacia mal la puntuacion. En un aguante el lastre y los segundos miden
 * lo mismo por dos caminos: cargas mas, aguantas menos, y el sistema pagaba
 * dos veces por una sola cualidad. En una serie de repeticiones no pasa: el
 * peso sube el listón de cada repeticion y el numero sigue contando aparte.
 *
 * La resistencia queda fuera a proposito. Es una prueba de volumen y su formato
 * puede medirse por tiempo, asi que meter lastre cambiaria que se esta midiendo
 * y haria incomparables AMRAP y "por tiempo".
 */
export const WEIGHTED_THEMES: readonly CompetitionTheme[] = ['DYNAMIC', 'DYNAMIC_WEIGHTED'];

/** Tematica en la que el lastre es obligatorio (RF-COMP-09). */
export const WEIGHT_REQUIRED_THEMES: readonly CompetitionTheme[] = ['DYNAMIC_WEIGHTED'];

/** Como se mide cada tematica; determina que campos pide el formulario. */
export const THEME_MEASUREMENT: Record<CompetitionTheme, 'REPS' | 'SECONDS' | 'REPS_OR_TIME'> = {
  DYNAMIC: 'REPS',
  STATIC: 'SECONDS',
  DYNAMIC_WEIGHTED: 'REPS',
  ENDURANCE: 'REPS_OR_TIME',
};

/** Formato por defecto de las competiciones de resistencia. */
export const DEFAULT_ENDURANCE_FORMAT: EnduranceFormat = 'AMRAP';

/** Limites de cordura de los valores declarados por el atleta. */
export const DECLARED_LIMITS = {
  MIN_REPS: 1,
  MAX_REPS: 1000,
  MIN_SECONDS: 1,
  MAX_SECONDS: 3600,
  MIN_ADDED_WEIGHT_KG: 0,
  MAX_ADDED_WEIGHT_KG: 120,
  MIN_BODY_WEIGHT_KG: 30,
  MAX_BODY_WEIGHT_KG: 200,
} as const;
